<template>
  <div id="car-info" class="content-section">
    <div class="section-title">车型信息</div>
    <div class="section-content" v-loading="loading">
      <StickyItem title="车型名称：" :content="modelName" />
      <div class="car-model-info">
        <!-- 数据展示 -->
        <div class="info-list">
          <div
            v-for="(item, index) in filteredInfoItems"
            :key="index"
            class="info-item"
            :class="{ 'full-width': item.fullWidth }"
          >
            <span class="label">{{ item.label }}</span>
            <template v-if="item.type === 'image'">
              <span v-if="carModelImages.length > 0" class="value">车型外观/内饰/空间</span>
              <span v-else class="value">--</span>
            </template>
            <span v-else class="value">{{ formattedData[item.key] }}</span>
          </div>
        </div>
        <!-- 车型图片网格展示 -->
        <div class="images-grid" v-if="carModelImages.length > 0">
          <div
            v-for="(img, index) in carModelImages"
            :key="index"
            class="image-item"
          >
            <el-image
              :src="img.url"
              fit="cover"
              :preview-src-list="carModelPicList"
              :initial-index="index"
            />
            <span class="image-label">{{ img.label }}</span>
          </div>
        </div>
      </div>
      <el-row>
        <el-col :span="12">
          <StickyItem title="车龄：" :content="ageNameDesc" />
        </el-col>
        <el-col :span="12">
          <StickyItem title="表显里程：" :content="mileageNameDesc" />
        </el-col>
        <el-col :span="12">
          <StickyItem title="是否有车证：" :content="carCertDesc" />
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { cloneDeep } from 'lodash'
import { use$to } from '@/utils/useUtils'
import StickyItem from './stickyItem.vue'
import { getCarLevelByModelId } from '@/api/domain/shop'
import { OPERATION_TYPE_ENUM_CONFIG } from '../../config/list'
import {
  judgeFuelConsumption,
  judgePureElectricDrivingRange,
} from '@/pages/shop/commodity/editDrawer/components/CarInfo/config.js'

const $to = use$to()

const props = defineProps({
  carInfo: {
    type: Object,
    default: () => ({}),
  },
})

const formData = ref({})
const loading = ref(false)

// 显示 纯电续航里程： 能源类型为 纯电动、插电式混动、增程式混动、油电混合
const showPureElectricDrivingRange = computed(() => {
  const { energyType } = formData.value
  return judgePureElectricDrivingRange(energyType)
})

// 显示 综合油耗： 能源类型为 汽油、插电式混动、增程式混动、油电混合、柴油、天然气
const showFuelConsumption = computed(() => {
  const { energyType } = formData.value
  return judgeFuelConsumption(energyType)
})

// 定义信息项配置
const infoItems = computed(() => [
  { label: '车型年款：', key: 'modelYear', unit: '款' },
  {
    label: '综合油耗：',
    key: 'fuelConsumption',
    unit: 'L/100km',
    show: showFuelConsumption.value,
  },
  {
    label: '纯电续航里程：',
    key: 'pureElectricDrivingRange',
    unit: 'km',
    show: showPureElectricDrivingRange.value,
  },
  {
    label: '运营类型：',
    key: 'operationType',
  },
  {
    label: '网约车接单资质：',
    key: 'carLevelName',
    show: formData.value?.operationType === 10,
  },
  {
    label: '车型图片：',
    key: 'carModelPic',
    type: 'image',
    fullWidth: true,
  },
])

// 添加过滤后的信息项计算属性
const filteredInfoItems = computed(() => {
  return infoItems.value.filter((item) => item.show !== false)
})

const modelName = computed(() => {
  const { modelName: name, modelYear, brandName, seriesName } = formData.value
  if (!modelName || !modelYear) return '--'
  return `${name}(${brandName}·${seriesName} ${modelYear}款) `
})

const ageNameDesc = computed(() => {
  if (!props.carInfo) return '--'
  const { ageName = null } = props.carInfo
  if (!ageName) return '--'
  return ageName
})
const mileageNameDesc = computed(() => {
  if (!props.carInfo) return '--'
  const { mileageName } = props.carInfo
  if (!mileageName) return '--'
  return mileageName
})
const carCertDesc = computed(() => {
  if (!props.carInfo) return '--'
  const { carCert } = props.carInfo
  if (carCert === null) return '--'
  return carCert ? '有车证' : '无车证'
})

// 格式化显示值
const formatValue = (value, unit = '') => {
  if (!value && value !== 0) return '--'
  return unit ? `${value}${unit}` : value
}
// 车型等级查询结果标识  0：未查询到，1：查询到
const CAR_LEVEL_FLAG_ENUM = {
  NOT_QUERY: 0,
  QUERY: 1,
}

// 车型图片列表（用于预览）
const carModelPicList = computed(() => (formData.value?.carModelPics || []).map((item) => item.value))

// 车型图片数据（带标签）
const carModelImages = computed(() => (formData.value?.carModelPics || []).map((item) => ({
  url: item.value,
  label: item.key,
})))

const formattedData = computed(() => {
  const { carLevelFlag, carLevelName } = formData.value
  const baseData = {
    ...formData.value,
    operationType:
      OPERATION_TYPE_ENUM_CONFIG[formData.value?.operationType] || '',
    carLevelName:
      CAR_LEVEL_FLAG_ENUM.QUERY === carLevelFlag && carLevelName
        ? carLevelName
        : '该车型暂无可接单类型，请与合作网约平台进行确认',
  }

  // 遍历 infoItems 为每个字段添加单位
  infoItems.value.forEach((item) => {
    if (item.unit && baseData[item.key] !== undefined) {
      baseData[item.key] = formatValue(baseData[item.key], item.unit)
    }
  })

  return baseData
})

const handleCarModelInfo = async () => {
  const { modelId } = props.carInfo
  if (!modelId) return
  loading.value = true
  const [err, res] = await $to(
    getCarLevelByModelId({
      modelId,
      cityCode: props.carInfo.cityCode,
    }),
  )
  loading.value = false
  if (err || res?.code !== 1) return
  const { data = {} } = res
  formData.value = cloneDeep(data)
}

watch(
  () => props.carInfo,
  () => {
    handleCarModelInfo()
  },
)
</script>

<style lang="scss" scoped>
.content-section {
  background: #ffffff;
  border-radius: 12px;
  opacity: 1;
  margin-bottom: 12px;

  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: #333333;
    padding: 8px 16px;
    line-height: 24px;
    letter-spacing: 0em;
    color: rgba(0, 0, 0, 0.9);
  }

  .section-content {
    padding: 0 16px 16px 16px;
  }
  .car-model-info {
    padding: 12px;
    background: #f3f3f4;
    border-radius: 8px;
    opacity: 1;
    min-height: 200px;
    background: #f3f3f4;

    .info-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 16px;

      .info-item {
        width: calc(50% - 8px);
        display: flex;
        align-items: flex-start;

        &.full-width {
          width: 100%;
        }

        .label {
          width: auto;
          color: #606266;
          margin-right: 4px;
          flex-shrink: 0;
          text-align: right;
        }

        .value {
          color: #303133;
          flex: 1;
        }

      }
    }

    .images-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
      margin-top: 12px;

      .image-item {
        display: flex;
        flex-direction: column;
        align-items: center;

        .el-image {
          width: 100%;
          height: 80px;
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid #e4e7ed;
          background: #fff;
        }

        .image-label {
          margin-top: 6px;
          font-size: 12px;
          color: #909399;
          text-align: center;
        }
      }
    }
  }
}
</style>
